import { Editor, NgxEditorModule } from 'ngx-editor';
import { Component, inject, input, OnInit, viewChild, ViewEncapsulation } from '@angular/core';
import { Plugin, PluginKey } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { AixInputComponent } from '../aix-input/aix-input.component';
import { AixModalComponent } from '../aix-modal/aix-modal.component';
import { AixDataTestingDirective } from '../../directives/data-testing/data-testing.directive';
import { isMarkActive } from 'ngx-editor/helpers';

@Component({
    selector: 'aix-rich-text-add-link',
    templateUrl: './aix-rich-text-add-link.component.html',
    styleUrls: ['./aix-rich-text-add-link.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        NgxEditorModule,
        ReactiveFormsModule,
        AixInputComponent,
        AixModalComponent,
        AixDataTestingDirective
    ]
})
export class AixRichTextAddLinkComponent implements OnInit {
    private _fb = inject(UntypedFormBuilder);
    _form: UntypedFormGroup;

    editor = input.required<Editor>();
    addLinkModal = viewChild.required<AixModalComponent>('addLinkModal');

    isActive = false;

    addLinkModalIsInvalid = (button: string) => this._form.invalid && button !== 'Cancel';
    private _editRange: { from: number; to: number } | null = null;

    constructor() {}

    ngOnInit(): void {
        this._form = new UntypedFormGroup({
            url: this._fb.control(null),
            text: this._fb.control(null)
        });

        const plugin = new Plugin({
            key: new PluginKey(`aix_link`),
            view: () => {
                return {
                    update: this.update.bind(this)
                };
            }
        });

        this.editor().registerPlugin(plugin);
    }

    addLinkModalSelected(e: string) {
        switch (e) {
            case 'Add Link':
                const { state, dispatch } = this.editor().view;
                const { schema } = state;
                const linkMark = schema.marks.link;

                if (!this._editRange) return;

                const { from, to } = this._editRange;

                let text = this._form.value.text.trim();
                let url = this._form.value.url.trim();
                let tr = state.tr;

                // Remove link if url is empty
                if (url === '') {
                    this.removeLink();
                    return;
                } else if (text === '') {
                    // Set url as text if the user removed the text
                    text = url;
                }

                // Check if the URL does not start with http:// or https://
                if (!/^https?:\/\//.test(url)) {
                    url = `https://${url}`;
                }

                // Replace text between from/to with the new text
                tr = tr.insertText(text, from, to);

                // Apply mark to text
                if (url) {
                    tr = tr.addMark(
                        from,
                        from + text.length,
                        linkMark.create({ href: url, target: '_blank' })
                    );
                }

                dispatch(tr);
                this.editor().view.focus();

                // Clear range selected
                this._editRange = null;
                break;
            default:
                break;
        }
    }

    onCloseModal() {
        this.isActive = false;
    }

    onClick(e: MouseEvent): void {
        this.addLinkModal().openModal();

        const { state } = this.editor().view;
        const sel = state.selection;
        let start = Math.min(sel.from, sel.to);
        let end = Math.max(sel.from, sel.to);
        let url: string | null = null;
        let textSelected = '';

        const hasLinkMark = (node: any) =>
            !!node.marks && node.marks.some((m: any) => m.type && m.type.name === 'link');

        if (sel.empty) {
            // If the user has not selected a text, we have to find the text with the link
            const $pos = sel.$from;
            const parent = $pos.parent;
            const parentStart = $pos.start();
            const childStarts: number[] = [];
            const childEnds: number[] = [];
            let running = parentStart;

            for (let i = 0; i < parent.childCount; i++) {
                childStarts.push(running);
                running += parent.child(i).nodeSize;
                childEnds.push(running);
            }

            let idx = childStarts.findIndex((s, i) => $pos.pos >= s && $pos.pos <= childEnds[i]);

            if (idx === -1) {
                const nodeBefore = $pos.nodeBefore;
                const nodeAfter = $pos.nodeAfter;
                const candidate = nodeBefore && hasLinkMark(nodeBefore) ? nodeBefore : nodeAfter;
                if (candidate) {
                    for (let i = 0; i < parent.childCount; i++) {
                        if (parent.child(i) === candidate) {
                            idx = i;
                            break;
                        }
                    }
                }
            }

            if (idx >= 0) {
                let left = idx;
                for (let i = idx - 1; i >= 0; i--) {
                    if (hasLinkMark(parent.child(i))) left = i;
                    else break;
                }

                let right = idx;
                for (let i = idx + 1; i < parent.childCount; i++) {
                    if (hasLinkMark(parent.child(i))) right = i;
                    else break;
                }

                start = childStarts[left];
                end = childEnds[right];
                textSelected = state.doc.textBetween(start, end);

                for (let i = left; i <= right; i++) {
                    const mark = parent
                        .child(i)
                        .marks.find((m: any) => m.type && m.type.name === 'link');
                    if (mark && mark.attrs && mark.attrs.href) {
                        url = mark.attrs.href;
                        break;
                    }
                }
            }
        } else {
            textSelected = state.doc.textBetween(start, end);
            state.doc.nodesBetween(start, end, node => {
                if (node.isText) {
                    const mark = node.marks.find((m: any) => m.type && m.type.name === 'link');
                    if (mark && mark.attrs && mark.attrs.href) {
                        url = mark.attrs.href;
                        return false;
                    }
                }
                return true;
            });
        }

        this._form.get('url')?.setValue(url || '');
        this._form.get('text')?.setValue(textSelected || '');

        this._editRange = { from: start, to: end };

        e.preventDefault();
    }

    update = (view: EditorView) => {
        const { state } = view;
        const { schema } = state;

        // Check if the link mark is active
        if (schema.marks.link) {
            this.isActive = isMarkActive(state, schema.marks.link);
        }
    };

    removeLink(): void {
        const { state, dispatch } = this.editor().view;
        const { from, to, empty } = state.selection;
        const linkMark = state.schema.marks.link;

        if (!linkMark) return;

        if (empty) {
            // Remove mark from the current word/node
            const resolvedPos = state.doc.resolve(from);
            const parent = resolvedPos.parent;
            const index = resolvedPos.index();

            const node = parent.child(index);
            if (!node || !linkMark.isInSet(node.marks)) {
                return;
            }

            const start = resolvedPos.start() + parent.childBefore(resolvedPos.parentOffset).offset;
            const end = start + node.nodeSize;

            const tr = state.tr.removeMark(start, end, linkMark);
            dispatch(tr);
        } else {
            // Remove mark only from the range selected
            const tr = state.tr.removeMark(from, to, linkMark);
            dispatch(tr);
        }

        this.editor().view.focus();
    }
}
